import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { delay, map } from 'rxjs/operators';
import {
  OpcionEnum,
  TipoParametroDto,
  ParametroCategoriaDto,
  UpdateParametroCategoriaDto,
} from '@models/tipoParametro/tipoParametroDto.model';
import { HttpClient } from '@angular/common/http';

export interface CategoriaMenuItem {
  nombre: string;
  icono: string;
  ruta: string;
}

@Injectable({
  providedIn: 'root',
})
export class ParametroConfiguracionService {
  private parametrosCache: ParametroCategoriaDto[] = [];

  constructor(private http: HttpClient) {}

  // GET /api/ParametroEmpresa/GetAll - Obtiene todos los parámetros
  private getAllParametros(): Observable<ParametroCategoriaDto[]> {
    return this.http
      .get<ParametroCategoriaDto[]>('/api/ParametroEmpresa/GetAll')
      .pipe(
        map((parametros) => {
          // Añadir valores por defecto si no vienen en la respuesta
          return parametros.map((param) => ({
            ...param,
            tipoParametro: {
              ...param.tipoParametro,
              esRequerido: param.tipoParametro.esRequerido ?? false,
              validacion: param.tipoParametro.validacion ?? '',
            },
          }));
        })
      );
  }

  // Extrae las categorías únicas del array de parámetros
  getCategorias(): Observable<CategoriaMenuItem[]> {
    return this.getAllParametros().pipe(
      map((parametros) => {
        this.parametrosCache = parametros; // Cachear para uso posterior

        // Extraer categorías únicas
        const categoriasUnicas = new Map<string, CategoriaMenuItem>();

        parametros.forEach((param) => {
          if (
            param.tipoParametro.visibleEnUI &&
            param.tipoParametro.categoria
          ) {
            const categoria = param.tipoParametro.categoria;
            if (!categoriasUnicas.has(categoria)) {
              categoriasUnicas.set(categoria, {
                nombre: categoria,
                icono: param.tipoParametro.iconoCategoria || 'settings',
                ruta: this.categoriaNombreToRuta(categoria),
              });
            }
          }
        });

        return Array.from(categoriasUnicas.values()).sort((a, b) =>
          a.nombre.localeCompare(b.nombre)
        );
      })
    );
  }

  // Convierte el nombre de categoría a una ruta amigable
  private categoriaNombreToRuta(categoria: string): string {
    return categoria
      .toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/[áäà]/g, 'a')
      .replace(/[éëè]/g, 'e')
      .replace(/[íïì]/g, 'i')
      .replace(/[óöò]/g, 'o')
      .replace(/[úüù]/g, 'u')
      .replace(/[ñ]/g, 'n')
      .replace(/[^a-z0-9-]/g, '');
  }

  // Filtra los parámetros por categoría
  getParametrosByCategoria(
    categoriaRuta: string
  ): Observable<ParametroCategoriaDto[]> {
    // Si tenemos cache, usar ese
    if (this.parametrosCache.length > 0) {
      return of(
        this.filtrarParametrosPorRuta(categoriaRuta, this.parametrosCache)
      );
    }

    // Si no hay cache, obtener todos los parámetros y filtrar
    return this.getAllParametros().pipe(
      map((parametros) => {
        this.parametrosCache = parametros;
        return this.filtrarParametrosPorRuta(categoriaRuta, parametros);
      })
    );
  }

  private filtrarParametrosPorRuta(
    categoriaRuta: string,
    parametros: ParametroCategoriaDto[]
  ): ParametroCategoriaDto[] {
    return parametros.filter((param) => {
      if (!param.tipoParametro.visibleEnUI) return false;

      const rutaCategoria = this.categoriaNombreToRuta(
        param.tipoParametro.categoria || ''
      );
      return rutaCategoria === categoriaRuta;
    });
  }

  // POST /api/ParametroEmpresa/Update
  updateParametro(parametro: TipoParametroDto): Observable<any> {
    return this.http.post('/api/ParametroEmpresa/Update', parametro);
  }

  // PUT /api/ParametroEmpresa/UpdateCollection
  updateParametros(
    parametros: UpdateParametroCategoriaDto[]
  ): Observable<boolean> {
    return this.http.put<boolean>(
      '/api/ParametroEmpresa/UpdateCollection',
      parametros
    );
  }

  // Método para limpiar cache (útil para refrescar datos)
  clearCache(): void {
    this.parametrosCache = [];
  }
}
